import React, { useCallback, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { VStack } from '@musinsa/mwms-ds'
import { FormProvider, useForm } from 'react-hook-form'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { omitBy } from 'lodash-es'
import { PageLayout } from '@/components/layout/PageLayout'
import TitleSection from '@/components/content/TitleSection'
import OutboundUnallocatedList from '@/pages/outbound/outboundUnallocated/components/OutboundUnallocatedList'
import OutboundUnallocatedForm, { getInitForm } from '@/pages/outbound/outboundUnallocated/components/OutboundUnallocatedForm'
import { useGetUnallocatedOrders } from '@/services/outbound/outboundUnallocated/useOutboundUnallocatedQuries'
import { GetOrdersReq } from '@/services/outbound/outboundWaitingStatus/types'
import { useExportExcelOnOrders } from '@/services/outbound/outboundWaitingStatus/useOutboundWaitingStatusQuries'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { GRID_SIZE } from '@/utils/grid/grid'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useShipperFieldSelection } from '@/components/system/ShipperSelector'
import { GetOrderOperationHistoriesReq } from '@/services/outbound/outboundUnallocated/types'
import { SHIPPER_TYPES } from '@/services/shipper/apis'

function OutboundUnallocated() {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<GetOrdersReq & { isShipperNamesSelectedAll?: boolean }>()

  const form = useForm<GetOrdersReq>({ defaultValues: getInitForm(parsedQs as GetOrderOperationHistoriesReq) })

  const [queryKey, setQueryKey] = useState(form.getValues())
  const [currentPage, setCurrentPage] = useState(0)

  const { isAllSelected, handleShipperFieldChange, resetShipperSelection, processShipperDataForSubmit } = useShipperFieldSelection<GetOrdersReq>({
    logisticsServiceTypes: parsedQs?.logisticsServiceTypes?.length === SHIPPER_TYPES.length,
    shipperNames: String(parsedQs?.isShipperNamesSelectedAll) === 'true',
  })

  const { onExportData } = useExportExcelOnOrders({ ...parsedQs, page: currentPage, size: GRID_SIZE.MEDIUM })
  const { data, refetch, isFetching } = useGetUnallocatedOrders({
    ...queryKey,
    page: currentPage,
    size: GRID_SIZE.MEDIUM,
  })

  const handleReset = useCallback(() => {
    form.reset(getInitForm())
    resetShipperSelection()
  }, [form, resetShipperSelection])

  const handleSubmit = useCallback(
    async (formData: GetOrdersReq) => {
      const processedData = processShipperDataForSubmit(formData)
      const query = omitBy(processedData, (value) => value === '') as GetOrdersReq
      MenuStorage.menuOnSession.setSearch({ ...formData, isShipperNamesSelectedAll: isAllSelected.shipperNames })

      // TODO: [SCOPE B] 아래 다건 입력 구조 데이터 타입을 배열 -> 스트링으로 변환 필요 (API 수정 후 아래 코드 삭제 필요)
      if (typeof query?.orderNumbers === 'string') {
        query.orderNumbers = [query?.orderNumbers]
      }
      if (typeof query?.orderOptionNumbers === 'string') {
        query.orderOptionNumbers = [query?.orderOptionNumbers]
      }
      if (typeof query?.barcodes === 'string') {
        query.barcodes = [query?.barcodes]
      }

      flushSync(() => {
        setQueryKey(query)
        setCurrentPage(0)
      })

      reload()
      await refetch()
      replaceQuery({ ...query, isShipperNamesSelectedAll: isAllSelected.shipperNames })
    },
    [processShipperDataForSubmit, reload, refetch, replaceQuery],
  )

  const onChangePage = useCallback(async (page: number) => {
    flushSync(() => setCurrentPage(page))
    await refetch()
  }, [])

  // 조회 훅이 `enabled: false` 라 invalidate 로는 안 돈다 — 출고지시 접수 후 명시적으로 다시 읽는다.
  const onReplanDone = useCallback(() => {
    refetch().catch(() => undefined)
  }, [refetch])

  return (
    <FormProvider {...form}>
      <PageLayout gap={16}>
        <VStack gap={12}>
          <TitleSection variant="legacy" title="미할당 처리" />
          <OutboundUnallocatedForm onSubmit={handleSubmit} isLoading={isFetching} onFieldChange={handleShipperFieldChange} onReset={handleReset} />
        </VStack>
        <OutboundUnallocatedList
          items={data?.items || []}
          pageInfo={data?.pageInfo}
          isLoading={isFetching}
          currentPage={currentPage}
          onChangePage={onChangePage}
          onExportData={onExportData}
          onReplanDone={onReplanDone}
        />
      </PageLayout>
    </FormProvider>
  )
}

export default OutboundUnallocated
